import { useMutation } from '@apollo/client';
import { FC, useState } from 'react';

import { cacheUtils } from '@eluve/apollo-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import {
  useAssignedTenantIdFromParams,
  useIsEluveAdmin,
} from '@eluve/session-helpers';
import { useDialog } from '@eluve/utility-hooks';

import {
  removeTenantUserMutation,
  setTenantUserExcludedMutation,
  updateTenantUserRoleMutation,
} from './operations';

type UserRowActionsProps = {
  userId: string;
  email: string;
  refetch: () => void;
  role: string;
  isExcluded: boolean;
  /**
   * Called after a successful exclude INSTEAD of `refetch`. With the default
   * hidden-excluded filter the row leaves the result set, so the caller
   * refreshes in a pagination-safe way (reset to page 1 or refetch in place)
   * without racing two requests.
   */
  onExcluded?: () => void;
};

type DialogAction = 'role' | 'remove' | 'exclude';

export const UserRowActions: FC<UserRowActionsProps> = ({
  userId,
  email,
  refetch,
  role,
  isExcluded,
  onExcluded,
}) => {
  const { isDialogOpen, setIsDialogOpen } = useDialog(false);
  const [dialogAction, setDialogAction] = useState<DialogAction>('role');

  const isEluveAdmin = useIsEluveAdmin();
  const isRoleMember = role === 'tenant-member';
  const tenantId = useAssignedTenantIdFromParams();

  const [removeUser] = useMutation(removeTenantUserMutation);
  const [updateUserRole] = useMutation(updateTenantUserRoleMutation);
  const [setUserExcluded] = useMutation(setTenantUserExcludedMutation);

  const toast = useToast();
  const { t } = useTranslation();

  const handleOnActionClick = (action: DialogAction) => {
    setDialogAction(action);
    setIsDialogOpen(true);
  };

  const handleSetExcluded = (excluded: boolean) => {
    setIsDialogOpen(false);
    setUserExcluded({
      variables: { tenantId, userId, isExcluded: excluded },
      onError: () => {
        toast.error(
          excluded
            ? t('tenantAdmin.users.excludeFailed')
            : t('tenantAdmin.users.includeFailed'),
          { description: t('tenantAdmin.users.emailDescription', { email }) },
        );
      },
      onCompleted: () => {
        if (excluded && onExcluded) {
          onExcluded();
        } else {
          refetch();
        }
      },
    });
  };

  const handleOnRemoveClick = () => {
    setIsDialogOpen(false);
    removeUser({
      variables: { userId, tenantId },
      optimisticResponse: {
        deleteTenantUsersByPk: {
          __typename: 'TenantUsers',
          tenantId,
          userId,
        },
      },
      onError: () => {
        toast.error(t('tenantAdmin.users.removeFailed'), {
          description: t('tenantAdmin.users.emailDescription', { email }),
        });
      },
      update: (cache) => {
        cacheUtils.evict({
          typeName: 'TenantUsers',
          key: { tenantId, userId },
        });
        cache.gc();
      },
    });
  };

  const handleOnRoleUpdateClick = () => {
    setIsDialogOpen(false);

    const updatedRole = isRoleMember ? 'tenant-admin' : 'tenant-member';

    updateUserRole({
      variables: {
        tenantId,
        userId,
        role: updatedRole,
      },
      optimisticResponse: {
        updateTenantUsersByPk: {
          __typename: 'TenantUsers',
          tenantId,
          userId,
          role: updatedRole,
        },
      },
      onError: () => {
        toast.error(t('tenantAdmin.users.roleChangeFailed'), {
          description: t('tenantAdmin.users.emailDescription', { email }),
        });
      },
      onCompleted: () => {
        refetch();
      },
    });
  };

  return (
    <Stack justify="end" gap={2} direction="horizontal">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            <Icon name="Ellipsis" size="auto" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          alignOffset={-4}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <DropdownMenuItem onClick={() => handleOnActionClick('role')}>
            <Icon
              name={isRoleMember ? 'ChevronsUp' : 'ChevronsDown'}
              size="lg"
            />
            <span>
              {isRoleMember
                ? t('tenantAdmin.users.changeToAdmin')
                : t('tenantAdmin.users.changeToMember')}
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              isExcluded
                ? handleSetExcluded(false)
                : handleOnActionClick('exclude')
            }
          >
            <Icon name={isExcluded ? 'Eye' : 'EyeOff'} size="lg" />
            <span>
              {isExcluded
                ? t('tenantAdmin.users.includeInUsageReports')
                : t('tenantAdmin.users.excludeFromUsageReports')}
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!isEluveAdmin}
            onClick={() => handleOnActionClick('remove')}
          >
            <Icon name="X" size="lg" />
            <span>{t('tenantAdmin.users.removeUser')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={isDialogOpen}>
        <DialogContent size="lg" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{t('tenantAdmin.common.areYouSure')}</DialogTitle>
          </DialogHeader>
          <Text color="secondary">
            {dialogAction === 'remove' &&
              t('tenantAdmin.users.removeConfirmation', { email })}
            {dialogAction === 'role' &&
              t('tenantAdmin.users.roleChangeConfirmation', {
                email,
                role: isRoleMember
                  ? t('tenantAdmin.users.roleAdmin')
                  : t('tenantAdmin.users.roleMember'),
              })}
            {dialogAction === 'exclude' &&
              t('tenantAdmin.users.excludeConfirmation')}
          </Text>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                if (dialogAction === 'remove') {
                  handleOnRemoveClick();
                } else if (dialogAction === 'exclude') {
                  handleSetExcluded(true);
                } else {
                  handleOnRoleUpdateClick();
                }
              }}
            >
              {dialogAction === 'remove' &&
                t('tenantAdmin.users.removeUserAction')}
              {dialogAction === 'role' &&
                t('tenantAdmin.users.changeRoleAction')}
              {dialogAction === 'exclude' &&
                t('tenantAdmin.users.excludeUserAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Stack>
  );
};
